// Mobile viewport sizing.
//
// `100vh` is the *large* viewport: the height the page would have if the URL
// bar were hidden. On iOS the bottom bar and on Android the top bar therefore
// overlap the app shell, hiding the header or the message composer. iOS also
// leaves the layout viewport untouched when the on-screen keyboard opens.
//
// visualViewport reports what the user can actually see in both cases, so the
// shell height follows it. Exposed as a CSS variable (not React state) so
// resize/scroll events do not re-render the whole tree.
//
// iOS also *scrolls* the visible area down to the focused composer when the
// keyboard opens. A shell pinned to the top of the page then sat above what
// was shown: only the composer stayed in view, over a blank screen
// (2026-10-22). So the shell is fixed and follows the visible area's top
// (`--app-top` = visualViewport.offsetTop).
const HEIGHT_VAR = '--app-height';
const TOP_VAR = '--app-top';

export function setupViewportHeight() {
  const viewport = window.visualViewport;

  const update = () => {
    // Ignore pinch-zoom: it shrinks the visual viewport but not the layout.
    if (viewport && viewport.scale !== 1) return;

    const height = viewport?.height ?? window.innerHeight;
    noteHeight(height);
    if (height > 0) {
      document.documentElement.style.setProperty(HEIGHT_VAR, `${height}px`);
    }
    const top = Math.max(0, viewport?.offsetTop ?? 0);
    document.documentElement.style.setProperty(TOP_VAR, `${top}px`);
  };

  update();

  viewport?.addEventListener('resize', update);
  viewport?.addEventListener('scroll', update);
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);

  return () => {
    viewport?.removeEventListener('resize', update);
    viewport?.removeEventListener('scroll', update);
    window.removeEventListener('resize', update);
    window.removeEventListener('orientationchange', update);
  };
}

// The on-screen keyboard's height (M69, D263): the emoji panel takes its
// place. iOS keeps the layout viewport when the keyboard opens, so
// innerHeight − visualViewport.height is the keyboard. Android Chrome with
// `interactive-widget=resizes-content` (index.html) shrinks both, so that
// difference is ~0 there; instead we compare with the tallest visible height
// seen at this width (the width doesn't change when the keyboard opens, but
// it does on rotation). The last real height is kept on the device, for when
// the keyboard is down.
const KEYBOARD_KEY = 'twinden:keyboardHeight';
/** Less than this is a browser bar moving, not a keyboard. */
export const KEYBOARD_MIN_PX = 120;
const KEYBOARD_WRITE_STEP_PX = 8;
const tallest = new Map<number, number>();

function noteHeight(height: number) {
  const width = window.innerWidth;
  if (height > (tallest.get(width) ?? 0)) tallest.set(width, height);
  const keyboard = Math.round(keyboardHeight());
  // Resize events come in bursts while the keyboard animates: write only a
  // real change (8 px or more), not every frame.
  if (
    keyboard >= KEYBOARD_MIN_PX &&
    Math.abs(keyboard - (lastKeyboardHeight() ?? 0)) >= KEYBOARD_WRITE_STEP_PX
  ) {
    try {
      localStorage.setItem(KEYBOARD_KEY, String(keyboard));
    } catch {
      // Private mode: the default height will do.
    }
  }
}

/** The keyboard's height now, or 0 when it is down. */
export function keyboardHeight(): number {
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  const visible = viewport.height;
  const byLayout = window.innerHeight - visible;
  const byTallest = (tallest.get(window.innerWidth) ?? visible) - visible;
  return Math.max(0, byLayout, byTallest);
}

/** The keyboard's height the last time it was up on this device, or null. */
export function lastKeyboardHeight(): number | null {
  try {
    const value = Number(localStorage.getItem(KEYBOARD_KEY));
    return value >= KEYBOARD_MIN_PX ? value : null;
  } catch {
    return null;
  }
}

/** For tests: forget the heights seen. */
export function resetKeyboardTracking() {
  tallest.clear();
}
